import { motion } from "framer-motion";
import { FileText, CheckCircle2, AlertCircle, Scale, ShieldAlert, Mail } from "lucide-react";
import SEO from "../components/SEO";

export default function Terms() {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <SEO
                title="Terms of Service"
                description="Terms of Service for blinqbooks. The rules and regulations for using our AI business platform."
            />

            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 dark:border-gray-700"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                                <Scale className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-primary dark:text-white">Terms of Service</h1>
                                <p className="text-sm text-secondary dark:text-gray-400">Last updated: December 26, 2025</p>
                            </div>
                        </div>

                        <div className="prose prose-emerald dark:prose-invert max-w-none space-y-8 text-secondary dark:text-gray-300 leading-relaxed">
                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-accent" /> 1. Acceptance of Terms
                                </h2>
                                <p>
                                    By accessing or using blinqbooks, you agree to be bound by these Terms of Service. If you are using the service on behalf of an organization, you agree to these terms for that organization.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-accent" /> 2. Description of Service
                                </h2>
                                <p>
                                    blinqbooks provides an AI-driven business suite including accounting, inventory management, POS, and website building tools. We reserve the right to modify or discontinue any part of the service at any time.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 text-accent" /> 3. User Responsibilities
                                </h2>
                                <p>You are responsible for:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Maintaining the confidentiality of your account credentials.</li>
                                    <li>Ensuring the accuracy of all financial and business data uploaded.</li>
                                    <li>Complying with local tax and business regulations.</li>
                                    <li>Not using the service for any illegal or fraudulent activities.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <ShieldAlert className="w-5 h-5 text-accent" /> 4. Limitation of Liability
                                </h2>
                                <p>
                                    blinqbooks is provided "as is". While we strive for absolute accuracy in our AI models and financial calculations, we are not liable for any business losses, data errors, or third-party breaches resulting from the use of our platform.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Scale className="w-5 h-5 text-accent" /> 5. Termination
                                </h2>
                                <p>
                                    We may suspend or terminate your access to blinqbooks if you violate these terms or for any other reason that protects the integrity of our platform.
                                </p>
                            </section>

                            <section className="p-6 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700">
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-accent" /> Questions?
                                </h2>
                                <p>
                                    If you have any questions about these Terms, please reach out to our legal department at <a href="mailto:legal@blinqbooks.com" className="text-accent hover:underline">legal@blinqbooks.com</a>.
                                </p>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
